import React from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Payroll as PayrollType } from "@common/types/payroll";
import { currencyFormatter } from "@common/helpers/currency";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { getEntityPayrolls } from "@lib/payroll";
// oxlint-disable-next-line no-restricted-imports -- Legacy migration debt — do not copy into new code.
import { Collapsible } from "@design-system/Collapsible/Collapsible";
import { HStack } from "@design-system/HStack/HStack";
import { SectionHeader } from "@design-system/Section/Section";
import { useEntity } from "../../entity/useEntity";
import { getPayrollQueryKey } from "../../../queryClient";
import css from "./Payroll.module.scss";
import { PayrollDetails } from "./PayrollDetails";
import { SyncGustoConnection } from "./SyncGustoConnection";
import { ContractorPaymentDetails } from "./ContractPaymentDetails";

function getTotalPayroll(payroll: PayrollType) {
    const { totals, contractorPaymentTotals } = payroll;

    const contractorPaymentTotal = contractorPaymentTotals
        ? contractorPaymentTotals.debitAmount
        : null;
    if (contractorPaymentTotal !== null) {
        return currencyFormatter.format(contractorPaymentTotal);
    }

    const payrollTotal = totals
        ? totals.companyDebit +
          totals.benefits +
          totals.employeeBenefitsDeductions
        : null;

    if (payrollTotal !== null) {
        return currencyFormatter.format(payrollTotal);
    }

    return null;
}

function getPayrollTitle(payroll: PayrollType) {
    const { processedDate, offCycle, contractorPaymentTotals } = payroll;
    const suffix = processedDate
        ? ` for ${format(processedDate, fullDateFullYearFormat)}`
        : "";

    if (offCycle) {
        return `Off-cycle Payroll${suffix}`;
    }

    if (contractorPaymentTotals) {
        return `Contract Payment${suffix}`;
    }

    return `Payroll for ${getPayrollPeriodString(payroll)}`;
}

function getPayrollPeriodString(payroll: PayrollType) {
    const { payPeriodStartDate, payPeriodEndDate } = payroll;

    if (!payPeriodStartDate || !payPeriodEndDate) {
        return "";
    }

    return (
        format(payPeriodStartDate, fullDateFullYearFormat) +
        " - " +
        format(payPeriodEndDate, fullDateFullYearFormat)
    );
}

export const Payroll: React.FC = () => {
    const entity = useEntity();
    const { data: payrolls } = useQuery({
        queryKey: getPayrollQueryKey(entity.id),
        queryFn: () => getEntityPayrolls(entity),
    });

    return (
        <section className="entity-payrolls mt-5">
            <HStack justify="between">
                <SectionHeader>
                    <h4>Payroll</h4>
                </SectionHeader>
                <SyncGustoConnection variant="primary" entity={entity}>
                    Sync Payroll
                </SyncGustoConnection>
            </HStack>

            <main className={css.payrollContainer}>
                {payrolls
                    ? payrolls.map((payroll) => (
                          <Collapsible
                              key={payroll.id}
                              className={css.payrollItem}
                              header={
                                  <HStack justify="between" align="center">
                                      <span className="period">
                                          {getPayrollTitle(payroll)}
                                      </span>
                                      <span className={css.payrollTotal}>
                                          {getTotalPayroll(payroll)}
                                      </span>
                                  </HStack>
                              }
                              stacked
                          >
                              {payroll.contractorPaymentTotals ? (
                                  <ContractorPaymentDetails
                                      totals={payroll.contractorPaymentTotals}
                                  />
                              ) : (
                                  <PayrollDetails totals={payroll.totals} />
                              )}
                          </Collapsible>
                      ))
                    : null}
            </main>
        </section>
    );
};
